/**
 * OnboardingComplete — full-screen celebratory transition shown after the
 * user finishes the wizard. Auto-redirects to `/` after the animation.
 */

import { useEffect } from "react";
import { motion } from "framer-motion";
import { SealCheck, Sparkle } from "@phosphor-icons/react";

const REDIRECT_MS = 2600;

export interface OnboardingCompleteProps {
  /** Optional override for the redirect target. */
  redirectTo?: string;
}

export function OnboardingComplete({ redirectTo = "/" }: OnboardingCompleteProps) {
  useEffect(() => {
    const t = setTimeout(() => {
      window.location.replace(redirectTo);
    }, REDIRECT_MS);
    return () => clearTimeout(t);
  }, [redirectTo]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-8 bg-background overflow-hidden"
      data-testid="onboarding-complete"
      aria-live="polite"
    >
      {/* Radial glow */}
      <motion.div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_center,hsl(var(--primary)/0.18),transparent_60%)]"
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      />

      {/* Floating sparkles */}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <motion.div
          key={i}
          aria-hidden
          className="absolute"
          initial={{ opacity: 0, x: 0, y: 0 }}
          animate={{
            opacity: [0, 1, 0],
            x: Math.cos((i / 6) * Math.PI * 2) * 180,
            y: Math.sin((i / 6) * Math.PI * 2) * 180,
          }}
          transition={{
            duration: 1.6,
            delay: 0.3 + i * 0.08,
            ease: "easeOut",
          }}
        >
          <Sparkle weight="fill" className="w-4 h-4 text-primary/70" />
        </motion.div>
      ))}

      {/* Check icon */}
      <motion.div
        initial={{ scale: 0, rotate: -90 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 220, damping: 15, delay: 0.1 }}
        className="relative"
      >
        <span className="absolute inset-0 animate-ping rounded-full bg-primary/30" />
        <span className="absolute inset-2 animate-pulse rounded-full bg-primary/20" />
        <SealCheck
          weight="fill"
          className="relative w-28 h-28 text-primary drop-shadow-[0_0_24px_hsl(var(--primary)/0.6)]"
        />
      </motion.div>

      <div className="relative flex flex-col items-center gap-2 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-4xl font-semibold tracking-tight"
        >
          Perfil completo.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.55 }}
          className="text-base text-muted-foreground max-w-md"
        >
          A IA agora replica seu jeito. Levando você ao painel…
        </motion.p>
      </div>

      {/* Loader bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="relative h-0.5 w-48 overflow-hidden rounded-full bg-muted"
      >
        <motion.div
          className="absolute inset-y-0 left-0 bg-primary"
          initial={{ width: "0%" }}
          animate={{ width: "100%" }}
          transition={{ duration: REDIRECT_MS / 1000, ease: "linear" }}
        />
      </motion.div>
    </motion.div>
  );
}
